import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember, UpdateGuest,
  GetCardTypes, AddCardType, UpdateCardType, GetTeamByEventId,
} from '../../api/api';
import { fullName, normaliseText, todayISO, CARD_KINDS, needsCard } from '../../utils/helpers';
import { rowFunctionIds, householdKey, extraPersonsFor } from '../../utils/headcount';

export const GUEST_TYPES = ['Family', 'Business'];

// The name printed on the invitation card:
//   Family   → the head of the family (with the courtesy prefix)
//   Business → the owner, with his company underneath
// A typed CardName always wins, so an unusual case can be written by hand.
export const cardNameOf = (row) => {
  if (String(row.CardName || '').trim()) return String(row.CardName).trim();
  const person = fullName(row) || row.GuestName || '';
  if (String(row.GuestType || 'Family') === 'Business') return person;
  return person;
};
export const cardSubtitleOf = (row) => {
  if (String(row.GuestType || 'Family') === 'Business') return String(row.CompanyName || '').trim();
  return String(row.Relation || row.GroupName || '').trim();
};

const typeFnIds = (t) => String(t.FunctionIds || '').split(',').map(v => v.trim()).filter(Boolean);
const csv = (v) => String(v || '').split(',').map(x => x.trim()).filter(Boolean);
const blankType = () => ({ CardTypeName: '', Kind: 'Card', FunctionIds: [], ForExtraCards: 0, ExtraFunctionIds: [] });
const RECEPTION = /reception/i;

// Invitation cards — one per household (family head's name) or business.
//   • Card types of the event (Reception card, Thread Ceremony insert …): a
//     household needs a type when invited to ANY of its functions → the ORDER
//     totals, with the extra cards added to the type marked ForExtraCards.
//   • Per card: extra cards (+ persons, counted in the Reception headcount),
//     who delivers it (a team member or a typed name), Pending / Delivered.
//   Every card field is written to all rows of the household, like CardName.
export function useCards(event) {
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = isAdmin || can('CanMembers');
  const canEdit = isAdmin || can('CanMembersEdit') || can('CanMembers');
  const canManageTypes = isAdmin;

  const [rows, setRows] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fnFilter, setFnFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [cardTypeFilter, setCardTypeFilter] = useState('');
  const [delivererFilter, setDelivererFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);      // the card whose text is open
  const [cardForm, setCardForm] = useState({ GuestType: 'Family', CompanyName: '', CardName: '' });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [typeForm, setTypeForm] = useState(null);    // null = closed; { ...blankType(), CardTypeId? }

  const refresh = useCallback(() => {
    setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader.then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load guests'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, toast]);
  const loadTypes = useCallback(() => {
    GetCardTypes({ EventId: event.EventId }).then(d => setCardTypes(Array.isArray(d) ? d : [])).catch(() => setCardTypes([]));
  }, [event]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => setFunctions([]));
    GetTeamByEventId({ EventId: event.EventId })
      .then(d => setTeam(Array.isArray(d) ? d : [])).catch(() => setTeam([]));
  }, [event]);

  // Every card (one per household), before the filters.
  const allCards = useMemo(() => {
    const byKey = new Map();
    rows.filter(needsCard).forEach(r => {
      const key = householdKey(r);
      if (!byKey.has(key)) byKey.set(key, { key, head: r, members: [], functionNames: new Set(), fnIds: new Set() });
      const card = byKey.get(key);
      // The family head (GuestId === FamilyId) is the name on the card.
      if (Number(r.FamilyId || 0) && String(r.GuestId) === String(r.FamilyId)) card.head = r;
      if (!card.members.some(m => String(m.GuestId) === String(r.GuestId))) card.members.push(r);
      rowFunctionIds(r).forEach(id => card.fnIds.add(String(id)));
      String(r.FunctionNames || r.FunctionName || '').split(',').map(v => v.trim()).filter(Boolean)
        .forEach(n => card.functionNames.add(n));
    });
    return Array.from(byKey.values()).map(c => {
      const extraCards = c.members.reduce((s, m) => Math.max(s, Number(m.ExtraCards || 0)), 0);
      const h = c.head;
      return {
        ...c,
        name: cardNameOf(h),
        subtitle: cardSubtitleOf(h),
        type: String(h.GuestType || 'Family'),
        mobile: h.MobileNumber || '',
        city: h.City || '',
        persons: c.members.length,
        functions: Array.from(c.functionNames),
        cardTypes: cardTypes.filter(t => typeFnIds(t).some(id => c.fnIds.has(id))),
        extraCards,
        extraPersonsTyped: Number(h.ExtraPersons || 0),
        extraPersons: extraPersonsFor(extraCards, h.ExtraPersons),
        deliveredByUserId: h.CardDeliveredByUserId || null,
        deliveredBy: String(h.CardDeliveredByName || '').trim(),
        status: h.CardStatus || 'Pending',
        deliveredDate: h.CardDeliveredDate || null,
      };
    }).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [rows, cardTypes]);

  const cards = useMemo(() => {
    let list = allCards;
    if (fnFilter) list = list.filter(c => c.fnIds.has(String(fnFilter)));
    if (typeFilter) list = list.filter(c => c.type === typeFilter);
    if (cardTypeFilter) list = list.filter(c => c.cardTypes.some(t => String(t.CardTypeId) === String(cardTypeFilter)));
    if (delivererFilter === '—') list = list.filter(c => !c.deliveredBy);
    else if (delivererFilter) list = list.filter(c => c.deliveredBy === delivererFilter);
    if (statusFilter) list = list.filter(c => c.status === statusFilter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(c => `${c.name} ${c.subtitle} ${c.mobile} ${c.city} ${c.deliveredBy}`.toLowerCase().includes(s));
    }
    return list;
  }, [allCards, fnFilter, typeFilter, cardTypeFilter, delivererFilter, statusFilter, q]);

  const counts = useMemo(() => ({
    cards: cards.length,
    family: cards.filter(c => c.type === 'Family').length,
    business: cards.filter(c => c.type === 'Business').length,
  }), [cards]);

  // How many of each card to ORDER (all households, not the filtered view).
  const orderTotals = useMemo(() => cardTypes.map(t => {
    const households = allCards.filter(c => c.cardTypes.some(x => x.CardTypeId === t.CardTypeId)).length;
    const extra = Number(t.ForExtraCards) === 1 ? allCards.reduce((s, c) => s + c.extraCards, 0) : 0;
    return { type: t, households, extra, total: households + extra };
  }), [cardTypes, allCards]);
  // The hosts' own family (Guest Type 'Own family'): counted at the functions,
  // but no card - left out of every card above. Distinct people.
  const ownFamily = useMemo(() => new Set(rows.filter(r => !needsCard(r)).map(r => String(r.GuestId))).size, [rows]);
  const withoutCard = useMemo(() => (cardTypes.length ? allCards.filter(c => !c.cardTypes.length).length : 0), [cardTypes, allCards]);
  const extraTotals = useMemo(() => ({
    cards: allCards.reduce((s, c) => s + c.extraCards, 0),
    persons: allCards.reduce((s, c) => s + c.extraPersons, 0),
  }), [allCards]);

  // Who delivers how many (and how many are done).
  const byDeliverer = useMemo(() => {
    const m = new Map();
    allCards.forEach(c => {
      const k = c.deliveredBy || '—';
      if (!m.has(k)) m.set(k, { name: k, cards: 0, delivered: 0 });
      const e = m.get(k);
      e.cards += 1 + c.extraCards;
      if (c.status === 'Delivered') e.delivered += 1 + c.extraCards;
    });
    return [...m.values()].sort((a, b) => (a.name === '—') - (b.name === '—') || a.name.localeCompare(b.name));
  }, [allCards]);

  const teamOptions = useMemo(() => team
    .map(t => ({ userId: t.UserId, name: [t.Name, t.Surname].filter(Boolean).join(' ').trim() || t.MobileNumber }))
    .filter(t => t.name)
    .sort((a, b) => a.name.localeCompare(b.name)), [team]);
  const delivererNames = useMemo(() => [...new Set([
    ...teamOptions.map(t => t.name), ...allCards.map(c => c.deliveredBy).filter(Boolean),
  ])].sort(), [teamOptions, allCards]);

  // ── Writes: one household = all its rows ─────────────────────────────────
  const writeCards = async (list, data, okMsg) => {
    const ids = list.flatMap(c => c.members.map(m => Number(m.GuestId))).filter(Boolean);
    if (!ids.length) return;
    try {
      await UpdateGuest({ data: { ...data, UpdatedBy: me }, where: `GuestId in (${ids.join(',')})` });
      setRows(rs => rs.map(r => (ids.includes(Number(r.GuestId)) ? { ...r, ...data } : r)));
      if (okMsg) toast(okMsg);
    } catch {
      toast('Could not save - check your connection');
    }
  };

  const setExtraCards = (card, value) => {
    const n = Math.max(0, Math.min(99, Number(value) || 0));
    if (n === card.extraCards) return;
    writeCards([card], { ExtraCards: n, ...(n ? {} : { ExtraPersons: null }) });
  };
  const setExtraPersons = (card, value) => {
    const n = Math.max(0, Math.min(999, Number(value) || 0));
    if (n === card.extraPersonsTyped) return;
    writeCards([card], { ExtraPersons: n || null });
  };
  // value: a team member's name (then their UserId is kept too), a typed name, or '' to clear.
  const deliveryData = (value) => {
    const name = normaliseText(String(value || '')).trim();
    if (!name) return { CardDeliveredByUserId: null, CardDeliveredByName: null };
    const member = teamOptions.find(t => t.name.toLowerCase() === name.toLowerCase());
    return { CardDeliveredByUserId: member ? member.userId : null, CardDeliveredByName: member ? member.name : name };
  };
  const setDeliverer = (card, value) => {
    if (normaliseText(String(value || '')).trim() === card.deliveredBy) return;
    writeCards([card], deliveryData(value));
  };
  const setStatus = (card, status) => writeCards([card], {
    CardStatus: status, CardDeliveredDate: status === 'Delivered' ? todayISO() : null,
  });

  // Bulk: tick cards, then give them all to one person / mark them delivered.
  const toggleSelect = (key) => setSelected(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const allVisibleSelected = cards.length > 0 && cards.every(c => selected.has(c.key));
  const toggleSelectAll = () => setSelected(s => {
    const n = new Set(s);
    if (allVisibleSelected) cards.forEach(c => n.delete(c.key)); else cards.forEach(c => n.add(c.key));
    return n;
  });
  const selectedCards = useMemo(() => allCards.filter(c => selected.has(c.key)), [allCards, selected]);
  const bulkDeliverer = async (value) => {
    if (!selectedCards.length) return;
    await writeCards(selectedCards, deliveryData(value), `${selectedCards.length} cards given to ${value || 'nobody'}`);
    setSelected(new Set());
  };
  const bulkStatus = async (status) => {
    if (!selectedCards.length) return;
    await writeCards(selectedCards, { CardStatus: status, CardDeliveredDate: status === 'Delivered' ? todayISO() : null },
      `${selectedCards.length} cards marked ${status}`);
    setSelected(new Set());
  };

  // ── The card text (name / business) ──────────────────────────────────────
  const openCard = (card) => {
    setEditing(card);
    setCardForm({
      GuestType: card.head.GuestType || 'Family',
      CompanyName: card.head.CompanyName || '',
      CardName: card.head.CardName || '',
    });
  };
  const setCardF = (k, v) => setCardForm(f => ({ ...f, [k]: ['CompanyName', 'CardName'].includes(k) ? normaliseText(v) : v }));
  const saveCard = async () => {
    if (!editing) return;
    setSaving(true);
    await writeCards([editing], { ...cardForm }, 'Card updated');
    setSaving(false);
    setEditing(null);
  };

  // ── Card types (admins) ──────────────────────────────────────────────────
  const openTypeAdd = () => setTypeForm(blankType());
  const openTypeEdit = (t) => setTypeForm({
    CardTypeId: t.CardTypeId, CardTypeName: t.CardTypeName || '', Kind: t.Kind || 'Card',
    FunctionIds: typeFnIds(t), ForExtraCards: Number(t.ForExtraCards) === 1 ? 1 : 0, ExtraFunctionIds: csv(t.ExtraFunctionIds),
  });
  const closeType = () => setTypeForm(null);
  const setTypeF = (k, v) => setTypeForm(f => ({ ...f, [k]: k === 'CardTypeName' ? normaliseText(v) : v }));
  const toggleTypeFunction = (id) => setTypeForm(f => {
    const s = String(id);
    const FunctionIds = f.FunctionIds.includes(s) ? f.FunctionIds.filter(x => x !== s) : [...f.FunctionIds, s];
    return { ...f, FunctionIds, ExtraFunctionIds: f.ExtraFunctionIds.filter(x => FunctionIds.includes(x)) };
  });
  // Which of the card's functions the extra-card guests come to (Reception).
  const toggleTypeExtraFunction = (id) => setTypeForm(f => {
    const s = String(id);
    return { ...f, ExtraFunctionIds: f.ExtraFunctionIds.includes(s) ? f.ExtraFunctionIds.filter(x => x !== s) : [...f.ExtraFunctionIds, s] };
  });
  const saveType = async () => {
    const f = typeForm;
    if (!String(f.CardTypeName).trim()) { toast('Give the card a name'); return; }
    if (!f.FunctionIds.length) { toast('Tick the function(s) this card is for'); return; }
    if (f.ForExtraCards && !f.ExtraFunctionIds.length) { toast('Tick the function(s) extra-card guests come to'); return; }
    const data = {
      CardTypeName: f.CardTypeName.trim(), Kind: f.Kind, FunctionIds: f.FunctionIds.join(','),
      ForExtraCards: f.ForExtraCards ? 1 : 0,
      ExtraFunctionIds: f.ForExtraCards ? f.ExtraFunctionIds.join(',') : null, UpdatedBy: me,
    };
    try {
      // Only one type can carry the extra cards.
      if (data.ForExtraCards) {
        const others = cardTypes.filter(t => Number(t.ForExtraCards) === 1 && t.CardTypeId !== f.CardTypeId);
        await Promise.all(others.map(t => UpdateCardType({ data: { ForExtraCards: 0, UpdatedBy: me }, where: `CardTypeId = ${Number(t.CardTypeId)}` })));
      }
      if (f.CardTypeId) {
        await UpdateCardType({ data, where: `CardTypeId = ${Number(f.CardTypeId)}` });
      } else {
        await AddCardType({ ...data, EventId: event.EventId, OrganiserId: event.OrganiserId, SortOrder: cardTypes.length + 1, Valid: 1, CreatedBy: me });
      }
      toast('Card type saved'); setTypeForm(null); loadTypes();
    } catch { toast('Could not save the card type'); }
  };
  const deleteType = async (t) => {
    if (!window.confirm(`Remove the card type "${t.CardTypeName}"? Guests are not affected.`)) return;
    try {
      await UpdateCardType({ data: { Valid: 0, UpdatedBy: me }, where: `CardTypeId = ${Number(t.CardTypeId)}` });
      loadTypes();
    } catch { toast('Could not remove'); }
  };
  // One click for the usual set-up: the Reception card for every function
  // except the thread ceremony, carrying the extra cards - whose guests come to
  // the Reception only (else the last function) - plus a Thread Ceremony insert
  // when the event has one.
  const thread = /thread|janeu|janoi|upanayan|munj/i;
  const addDefaultTypes = async () => {
    const isThread = (f) => thread.test(`${f.FunctionType || ''} ${f.FunctionTypeName || ''} ${f.FunctionName || ''}`);
    const main = functions.filter(f => !isThread(f)).map(f => String(f.FunctionId));
    const ins = functions.filter(isThread).map(f => String(f.FunctionId));
    const rec = functions.filter(f => !isThread(f) && RECEPTION.test([f.FunctionType, f.FunctionTypeName, f.FunctionName].join(' ')))
      .map(f => String(f.FunctionId));
    const extraIds = rec.length ? rec : main.slice(-1);
    try {
      if (main.length) await AddCardType({ CardTypeName: 'Reception Card', Kind: 'Card', FunctionIds: main.join(','), ForExtraCards: 1, ExtraFunctionIds: extraIds.join(','), EventId: event.EventId, OrganiserId: event.OrganiserId, SortOrder: 1, Valid: 1, CreatedBy: me, UpdatedBy: me });
      if (ins.length) await AddCardType({ CardTypeName: 'Thread Ceremony Insert', Kind: 'Insert', FunctionIds: ins.join(','), ForExtraCards: 0, EventId: event.EventId, OrganiserId: event.OrganiserId, SortOrder: 2, Valid: 1, CreatedBy: me, UpdatedBy: me });
      toast('Card types added - check their functions'); loadTypes();
    } catch { toast('Could not add the card types'); }
  };

  const print = () => window.print();
  const fnName = useCallback((id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '', [functions]);

  return {
    canView, canEdit, canManageTypes, cards, counts, loading, functions, event, fnName,
    fnFilter, setFnFilter, typeFilter, setTypeFilter, cardTypeFilter, setCardTypeFilter,
    delivererFilter, setDelivererFilter, statusFilter, setStatusFilter, q, setQ,
    cardTypes, orderTotals, withoutCard, ownFamily, extraTotals, byDeliverer, teamOptions, delivererNames,
    setExtraCards, setExtraPersons, setDeliverer, setStatus,
    selected, toggleSelect, allVisibleSelected, toggleSelectAll, selectedCount: selectedCards.length, bulkDeliverer, bulkStatus,
    editing, setEditing, cardForm, setCardF, saveCard, saving, openCard,
    typeForm, openTypeAdd, openTypeEdit, closeType, setTypeF, toggleTypeFunction, toggleTypeExtraFunction, saveType, deleteType, addDefaultTypes,
    CARD_KINDS, typeFnIds, print,
  };
}
